// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Dashboard Page

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscribeToDoc } from '../../services/firebase';

interface RollupSnapshot {
  orgId: string;
  date: string;
  itemCounts?: {
    all?: {
      open?: number;
      closedToday?: number;
      avBuyTimeDays?: number;
      avUnitPrice?: number;
      totalRevDay?: number;
    };
    userAssigned?: { open?: number };
    shared?: { open?: number };
  };
  listCounts?: { all?: number; userAssigned?: number; shared?: number };
}

interface OfferPerfSnapshot {
  all?: {
    uniqueOrgsReached?: number;
    avgOrgTakeRate?: number;
    avgOfferTakeRate?: number;
    offersDelivered?: number;
    avgUnitPrice?: number;
    totalRevDay?: number;
  };
  requested?: { offerCount?: number };
  unrequested?: { offerCount?: number };
}

export default function DashboardPage() {
  const { activeOrgId, activeOrg } = useAuth();
  const [rollup, setRollup] = useState<RollupSnapshot | null>(null);
  const [offerPerf, setOfferPerf] = useState<OfferPerfSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!activeOrgId) return;
    setIsLoading(true);

    const unsub1 = subscribeToDoc<RollupSnapshot>(
      `org_list_rollups/${activeOrgId}/snapshots/${today}`,
      data => { setRollup(data); setIsLoading(false); }
    );
    const unsub2 = subscribeToDoc<OfferPerfSnapshot>(
      `offer_performance_rollups/${activeOrgId}/daily/${today}`,
      data => setOfferPerf(data)
    );

    return () => { unsub1(); unsub2(); };
  }, [activeOrgId, today]);

  const kpis = [
    { label: 'Open Items',
      value: rollup?.itemCounts?.all?.open?.toLocaleString() ?? '—',
      color: 'var(--color-primary)' },
    { label: 'Closed Today',
      value: rollup?.itemCounts?.all?.closedToday?.toLocaleString() ?? '—',
      color: 'var(--color-text)' },
    { label: 'Avg Buy Time',
      value: rollup?.itemCounts?.all?.avBuyTimeDays
        ? `${rollup.itemCounts.all.avBuyTimeDays}d` : '—',
      color: 'var(--color-text)' },
    { label: 'Avg Unit Price',
      value: rollup?.itemCounts?.all?.avUnitPrice
        ? `$${rollup.itemCounts.all.avUnitPrice.toFixed(2)}` : '—',
      color: 'var(--color-text)' },
    { label: 'Total Rev (1d)',
      value: rollup?.itemCounts?.all?.totalRevDay
        ? `$${rollup.itemCounts.all.totalRevDay.toLocaleString()}` : '—',
      color: 'var(--color-success)' },
    { label: 'Orgs Reached',
      value: offerPerf?.all?.uniqueOrgsReached?.toLocaleString() ?? '—',
      color: 'var(--color-info)' },
    { label: 'Org Take Rate',
      value: offerPerf?.all?.avgOrgTakeRate
        ? `${Math.round(offerPerf.all.avgOrgTakeRate * 100)}%` : '—',
      color: 'var(--color-success)' },
    { label: 'Offer Take Rate',
      value: offerPerf?.all?.avgOfferTakeRate
        ? `${Math.round(offerPerf.all.avgOfferTakeRate * 100)}%` : '—',
      color: 'var(--color-success)' },
  ];

  const reqCount  = offerPerf?.requested?.offerCount  ?? 0;
  const unreqCount = offerPerf?.unrequested?.offerCount ?? 0;
  const total = reqCount + unreqCount || 1;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 2 }}>Dashboard</h1>
          <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            {activeOrg?.name} · {today}
          </p>
        </div>
        <Link to="/offers/new" className="btn btn-success">+ New Offer</Link>
      </div>

      {/* KPI Grid */}
      {isLoading ? (
        <div className="empty-state"><span className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 12, marginBottom: 24 }}>
          {kpis.map(kpi => (
            <div key={kpi.label} className="card" style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-secondary)',
                marginBottom: 6, fontWeight: 500 }}>
                {kpi.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color }}>
                {kpi.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick links */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Requested vs Unrequested</span>
            <Link to="/analytics/offers"
              style={{ fontSize: 12, color: 'var(--color-info)' }}>
              View report →
            </Link>
          </div>
          <div style={{ padding: '12px 14px' }}>
            <div className="compare-bar-outer">
              <div className="compare-header">Offer volume split</div>
              <div className="compare-row">
                <span className="compare-label"
                  style={{ color: 'var(--color-info)' }}>Requested</span>
                <div className="compare-bar-wrap">
                  <div className="compare-fill"
                    style={{ width: `${(reqCount / total) * 100}%`,
                      background: 'var(--color-info)' }} />
                </div>
                <span className="compare-val" style={{ color: 'var(--color-info)' }}>
                  {reqCount.toLocaleString()}
                </span>
              </div>
              <div className="compare-row">
                <span className="compare-label"
                  style={{ color: 'var(--color-warning)' }}>Unrequested</span>
                <div className="compare-bar-wrap">
                  <div className="compare-fill"
                    style={{ width: `${(unreqCount / total) * 100}%`,
                      background: '#f9a825' }} />
                </div>
                <span className="compare-val" style={{ color: 'var(--color-warning)' }}>
                  {unreqCount.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">List Associations</span>
            <Link to="/analytics/items"
              style={{ fontSize: 12, color: 'var(--color-info)' }}>
              View report →
            </Link>
          </div>
          <div style={{ padding: '12px 14px' }}>
            {[
              { label: 'All Lists',      value: rollup?.listCounts?.all },
              { label: 'User-Assigned',  value: rollup?.listCounts?.userAssigned },
              { label: 'Shared',         value: rollup?.listCounts?.shared },
            ].map(row => (
              <div key={row.label} className="metric-row">
                <span className="metric-label">{row.label}</span>
                <span className="metric-val">
                  {row.value?.toLocaleString() ?? '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
